import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  auditApi,
  authApi,
  clusterApi,
  dashboardApi,
  idpApi,
  namespacesApi,
  rolesApi,
  settingsApi,
  usersApi,
  type AuditListParams,
  type KubeconfigOptions,
  type UsersListParams,
} from './endpoints';
import type {
  AppSettings,
  AuditEvent,
  Binding,
  Identity,
  IdpConfig,
  IssuedToken,
  Namespace,
  Paginated,
  Role,
  GroupRoleMapping,
} from './schemas';

export const keys = {
  me: ['auth', 'me'] as const,
  users: (params: UsersListParams) => ['users', params] as const,
  user: (id: string) => ['users', id] as const,
  bindings: (id: string) => ['users', id, 'bindings'] as const,
  tokens: (id: string) => ['users', id, 'tokens'] as const,
  namespaces: () => ['namespaces'] as const,
  roles: () => ['roles'] as const,
  clusterBindableRoles: () => ['cluster-bindable-roles'] as const,
  bindableRoles: () => ['bindable-roles'] as const,
  audit: (params: AuditListParams) => ['audit', params] as const,
  idp: () => ['admin', 'idp'] as const,
  settings: () => ['settings'] as const,
};

function toArray<T>(data: Paginated<T> | T[] | undefined): T[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.items || [];
}

export function useMeQuery() {
  return useQuery<Identity>({
    queryKey: keys.me,
    queryFn: authApi.me,
    retry: false,
    staleTime: 60_000,
  });
}

export function useUsersQuery(params: UsersListParams = {}) {
  return useQuery({
    queryKey: keys.users(params),
    queryFn: () => usersApi.list(params),
    select: (d) => ({ items: toArray<Identity>(d), total: 'total' in d ? d.total : undefined }),
    staleTime: 15_000,
  });
}

export function useUserQuery(id: string) {
  return useQuery<Identity>({
    queryKey: keys.user(id),
    queryFn: () => usersApi.get(id),
    enabled: !!id,
  });
}

export function useBindingsQuery(id: string) {
  return useQuery<Binding[]>({
    queryKey: keys.bindings(id),
    queryFn: () => usersApi.bindings(id),
    enabled: !!id,
  });
}

export function useNamespacesQuery() {
  return useQuery({
    queryKey: keys.namespaces(),
    queryFn: namespacesApi.list,
    select: (d) => toArray<Namespace>(d),
    staleTime: 30_000,
  });
}

export function useRolesQuery() {
  return useQuery<Role[]>({
    queryKey: keys.roles(),
    queryFn: rolesApi.list,
    staleTime: 60_000,
  });
}

export function useDashboardStats(days: number) {
  return useQuery({
    queryKey: ['dashboard', 'stats', days] as const,
    queryFn: () => dashboardApi.stats(days),
    staleTime: 30_000,
  });
}

export function useClusterBindableRolesQuery() {
  return useQuery<string[]>({
    queryKey: keys.clusterBindableRoles(),
    queryFn: clusterApi.bindableRoles,
    staleTime: 60_000,
  });
}

export function useBindableRolesQuery() {
  return useQuery<string[]>({
    queryKey: keys.bindableRoles(),
    queryFn: clusterApi.namespacedBindableRoles,
    staleTime: 60_000,
  });
}

export function useAuditQuery(params: AuditListParams = {}) {
  return useQuery({
    queryKey: keys.audit(params),
    queryFn: () => auditApi.list(params),
    select: (d) => ({ items: toArray<AuditEvent>(d), total: 'total' in d ? d.total : undefined }),
    staleTime: 10_000,
  });
}

export function useIdpQuery() {
  return useQuery<IdpConfig>({
    queryKey: keys.idp(),
    queryFn: idpApi.get,
    staleTime: 60_000,
  });
}

export function useUserDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUserCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Identity> & { password?: string; accountType?: string }) => usersApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useBindingCreate(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { namespace?: string; roleName: string; roleKind?: string; scope?: 'namespaced' | 'cluster' }) =>
      usersApi.createBinding(userId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.bindings(userId) }),
  });
}

export function useBindingDelete(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bindingId: string) => usersApi.deleteBinding(userId, bindingId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.bindings(userId) }),
  });
}

export function useRotateToken() {
  return useMutation({ mutationFn: (id: string) => usersApi.rotateToken(id) });
}

export function useTokens(userId: string, enabled = true) {
  return useQuery<IssuedToken[]>({
    queryKey: keys.tokens(userId),
    queryFn: () => usersApi.tokens(userId),
    enabled: enabled && !!userId,
  });
}

export function useRevokeToken(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) => usersApi.revokeToken(userId, tokenId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tokens(userId) }),
  });
}

export function useRevokeAllTokens(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => usersApi.revokeAllTokens(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tokens(userId) }),
  });
}

export function useKubeconfig() {
  return useMutation({
    mutationFn: ({ id, ...opts }: { id: string } & KubeconfigOptions) => usersApi.kubeconfig(id, opts),
  });
}

// Instance settings. Readable by any signed-in user (the credentials form
// prefills from them); the update call is admin-only server-side.
export function useSettingsQuery() {
  return useQuery<AppSettings>({
    queryKey: keys.settings(),
    queryFn: () => settingsApi.get(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSettingsUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<AppSettings>) => settingsApi.update(data),
    onSuccess: (next) => qc.setQueryData(keys.settings(), next),
  });
}

export function useResetCredentials() {
  return useMutation({
    mutationFn: (vars: { id: string; password?: string }) => usersApi.resetCredentials(vars.id, vars.password),
  });
}

export function useUserUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; data: Partial<Identity> }) => usersApi.update(vars.id, vars.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: keys.user(vars.id) });
    },
  });
}

export function useIdpUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<IdpConfig>) => idpApi.update(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.idp() }),
  });
}

export function useIdpTest() {
  return useMutation({ mutationFn: (data?: Partial<IdpConfig>) => idpApi.test(data) });
}

export function useIdpMappingCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: GroupRoleMapping) => idpApi.mappings.create(m),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.idp() }),
  });
}

export function useIdpMappingDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => idpApi.mappings.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.idp() }),
  });
}
